import { useMemo } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Form, useNotify } from "react-admin";
import dataProvider from "server/dataProvider";
import { studentRequests } from "api/studentApi";
import { useNotifyError } from "utils/useNotifyError";
import { groupLabel, semesterOfGroup } from "./utils";
import SemesterAttestationsForm from "./SemesterAttestationsForm";

// Only the fields the backend accepts - the rest of the response is display data.
const toFormRow = (attestation, disciplineId) => ({
  id: attestation.id,
  disciplineId,
  classTypeId: attestation.classTypeId,
  classTypeNameBg: attestation.classTypeNameBg,
  status: attestation.status,
  professorEmployeeId: attestation.professorEmployeeId ?? null,
  note: attestation.note ?? "",
});

const normalizeId = value => (value === "" || value === undefined ? null : value);

// The backend rejects two attestations with the same discipline, class type and professor in a semester.
const duplicateKey = row => [row.disciplineId, row.classTypeId, normalizeId(row.professorEmployeeId)].join("|");

const SemesterAccordion = ({ studentEducationId, group, canManage, expanded, onToggle, onSaved }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const { disciplines } = group;

  const rows = useMemo(
    () =>
      disciplines.flatMap(discipline =>
        discipline.attestations.map(attestation => toFormRow(attestation, discipline.disciplineId)),
      ),
    [disciplines],
  );

  const isChanged = row => {
    const original = rows.find(candidate => candidate.id === row.id);
    if (!row.id || !original) return true;
    return (
      row.status !== original.status ||
      normalizeId(row.professorEmployeeId) !== original.professorEmployeeId ||
      (row.note ?? "").trim() !== original.note.trim()
    );
  };

  // Rows left untouched are not sent - the endpoint upserts only what it receives.
  const handleSave = async values => {
    const keys = (values?.attestations ?? []).map(duplicateKey);
    if (keys.some((key, index) => keys.indexOf(key) !== index)) {
      notify("Има повтарящи се заверки за една и съща дисциплина, тип занятие и преподавател!", { type: "error" });
      return;
    }

    const attestations = (values?.attestations ?? []).filter(isChanged).map(row => ({
      id: row.id ?? null,
      disciplineId: row.disciplineId,
      classTypeId: row.classTypeId,
      status: row.status,
      professorEmployeeId: normalizeId(row.professorEmployeeId),
      note: row.note?.trim() ? row.note.trim() : null,
    }));

    if (!attestations.length) {
      notify("Няма променени заверки за записване", { type: "info" });
      return;
    }

    try {
      await dataProvider.customRequest(
        studentRequests.updateStudentEducationAttestationsBySemester(studentEducationId, semesterOfGroup(group)),
        { attestations },
        "PUT",
      );
      notify("Заверките бяха записани успешно", { type: "success" });
      await onSaved();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    // Collapsed groups stay unmounted so the page does not hold a form (and a professor
    // autocomplete per row) for every semester of the curriculum at once.
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: "bold" }}>{groupLabel(group)}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Form
          onSubmit={handleSave}
          defaultValues={{ attestations: rows }}
        >
          <SemesterAttestationsForm
            disciplines={disciplines}
            rows={rows}
            canManage={canManage}
          />
        </Form>
      </AccordionDetails>
    </Accordion>
  );
};

export default SemesterAccordion;
